import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { IconX } from "@tabler/icons-react";

import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import service from "utils/service";
import useFetchData from "utils/customFetchData";
import { reduxSliceData } from "store/slices/reduxData";

interface CreateCustomerModalProps {
  visible: boolean;
  onClose: () => void;
}

const EASEBUZZ_SCRIPT =
  "https://ebz-static.s3.ap-south-1.amazonaws.com/easecheckout/v2.0.0/easebuzz-checkout-v2.min.js";

const EASEBUZZ_SUCCESS_API = service.API_URL.wallet_master.ezzbuzzSuccess;

const RechargeWalletEzzBuzz: React.FC<CreateCustomerModalProps> = ({
  visible,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();

  const { addData } = useFetchData();

  const loginCustomer = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("loginCustomer") || "[]");
    } catch {
      return [];
    }
  }, []);

  const reduxBag = useSelector(
    (state: any) => state.reduxData?.reduxData ?? {}
  );

  const getWalletData = reduxBag?.getWalletData;

  const [discount, setDiscount] = useState(0);
  const [balance, setBalance] = useState(0);
  const [loader, setLoader] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [amount, setAmount] = useState(1500);

  const predefinedAmounts = [500, 1000, 1500, 5000, 10000];

  const customerId =
    loginCustomer?.[0]?.UserData?.[0]?.CUSTOMER_ID;

  const customerName =
    loginCustomer?.[0]?.UserData?.[0]?.NAME || "";

  const customerEmail =
    loginCustomer?.[0]?.UserData?.[0]?.EMAIL_ID || "";

  const customerMobile =
    loginCustomer?.[0]?.UserData?.[0]?.MOBILE_NO || "";

  // ---------------------------------------------------------
  // Payment gateway charge
  // ---------------------------------------------------------

  const GATEWAY_PERCENT = 2.5;

  const gatewayCharge = 0;

  const payableAmount = Number(
    amount.toFixed(2)
  );

  // ---------------------------------------------------------
  // Get wallet balance
  // ---------------------------------------------------------

  useEffect(() => {
    if (getWalletData) {
      const walletRows = Array.isArray(getWalletData?.data)
        ? getWalletData.data
        : Array.isArray(getWalletData?.apiData)
        ? getWalletData.apiData
        : Array.isArray(getWalletData)
        ? getWalletData
        : [];

      const fetchedBalance = walletRows
        .map((item: any) => Number(item.BALANCE || 0))
        .reduce(
          (acc: number, walletBalance: number) =>
            acc + walletBalance,
          0
        );

      setBalance(fetchedBalance);
    }
  }, [getWalletData]);

  // ---------------------------------------------------------
  // Load Easebuzz SDK
  // ---------------------------------------------------------

  useEffect(() => {
    const existingScript = document.querySelector(
      `script[src="${EASEBUZZ_SCRIPT}"]`
    );

    if (existingScript) {
      setIsScriptLoaded(true);
      return;
    }

    const script = document.createElement("script");

    script.src = EASEBUZZ_SCRIPT;
    script.async = true;

    script.onload = () => {
      setIsScriptLoaded(true);
    };

    script.onerror = () => {
      setIsScriptLoaded(false);

      enqueueSnackbar("Failed to load Easebuzz payment gateway.", {
        variant: "error",
      });
    };

    document.body.appendChild(script);

    return () => {
      // Don't remove the script here.
      // Keeping it loaded avoids reloading the SDK every time
      // the recharge dialog opens/closes.
    };
  }, [enqueueSnackbar]);

  // ---------------------------------------------------------
  // Easebuzz Payment
  // ---------------------------------------------------------

  const handleEasebuzzPayment = useCallback(async () => {
    if (!isScriptLoaded) {
      enqueueSnackbar(
        "Payment gateway is not ready yet. Please wait a moment and try again.",
        {
          variant: "error",
        }
      );

      return;
    }

    if (!customerId) {
      enqueueSnackbar("Customer profile is missing.", {
        variant: "error",
      });

      return;
    }

    if (!customerName || !customerEmail || !customerMobile) {
      enqueueSnackbar("Customer details are incomplete.", {
        variant: "error",
      });

      return;
    }

    if (amount < 250 || amount > 50000) {
      enqueueSnackbar(
        "Please enter an amount between ₹250 and ₹50,000.",
        {
          variant: "error",
        }
      );

      return;
    }

    try {
      setLoader(true);

      // -----------------------------------------------------
      // Create Easebuzz recharge order
      // -----------------------------------------------------

      const body = {
        FIRST_NAME: customerName,
        EMAIL_ID: customerEmail,
        AMOUNT: payableAmount,
        MOBILE_NO: customerMobile,
      };


      const walletMaster = (service as any)?.API_URL?.wallet_master;

      const createEasebuzzOrderEndpoint = walletMaster?.createRechargeOrderForEasebuzz;

      const walletData = await addData(
        body,
        createEasebuzzOrderEndpoint
      );

      // console.log("Easebuzz order response:", walletData);

      const responseData =
        walletData?.apiData ??
        walletData?.data ??
        walletData;

      // -----------------------------------------------------
      // Validate Easebuzz order response
      // -----------------------------------------------------

      if (
        !responseData?.key ||
        !responseData?.access_key
      ) {
        enqueueSnackbar(
          responseData?.message,
          {
            variant: "error",
          }
        );

        return;
      }

      // -----------------------------------------------------
      // Initialize Easebuzz
      // -----------------------------------------------------

      const EasebuzzCheckout = (window as any)
        ?.EasebuzzCheckout;

      if (!EasebuzzCheckout) {
        enqueueSnackbar(
          "Easebuzz payment gateway is unavailable.",
          {
            variant: "error",
          }
        );

        return;
      }

      const easebuzzCheckout = new EasebuzzCheckout(
        responseData.key,
        "prod"
      );

      // -----------------------------------------------------
      // Easebuzz options
      // -----------------------------------------------------

      const options = {
        access_key: responseData.access_key,

        onResponse: async (response: any) => {
          // console.log("Easebuzz Response:", response);

          try {
            setLoader(true);
            
            if(response.status === 'success'){

              const backendResponse = await addData(
              response,
              service.API_URL.wallet_master.ezzbuzzSuccess
            );

            if (
              backendResponse.code == 200
            ) {
              dispatch(
                reduxSliceData({
                  key: "paymentSuccess",
                  data: true,
                })
              );

              setShowSuccessModal(true);

              enqueueSnackbar(
                "Payment successful!",
                {
                  variant: "success",
                }
              );
            } else {
              enqueueSnackbar(
                "Payment failed or was cancelled.",
                {
                  variant: "error",
                }
              );
            }

            }else{
              const failResponse = await addData(
                response,
                service.API_URL.wallet_master.ezzbuzzFail
              );
              if(failResponse.code == 200){
                enqueueSnackbar(
                  "Payment failed or was cancelled.",
                  {
                    variant: "error",
                  }
                );
              }else{
                enqueueSnackbar(
                  "Payment failed due to some technical issue, please contact admin",
                  {
                    variant: "error",
                  }
                );
              }
            }

            
          } catch (error) {
            console.error(
              "Failed to call Easebuzz backend:",
              error
            );

            enqueueSnackbar(
              "Payment was completed, but we could not verify it with the server.",
              {
                variant: "error",
              }
            );
          } finally {
            setLoader(false);
          }
        },

        theme: "#123456",
      };

      // -----------------------------------------------------
      // Start Easebuzz checkout
      // -----------------------------------------------------

      easebuzzCheckout.initiatePayment(options);
    } catch (error) {
      console.error(
        "Error initiating Easebuzz payment:",
        error
      );

      enqueueSnackbar(
        "Error occurred while initiating payment.",
        {
          variant: "error",
        }
      );
    } finally {
      setLoader(false);
    }
  }, [
    addData,
    amount,
    customerEmail,
    customerId,
    customerMobile,
    customerName,
    dispatch,
    enqueueSnackbar,
    isScriptLoaded,
    payableAmount,
  ]);

  // ---------------------------------------------------------
  // Form submit
  // ---------------------------------------------------------

  const handleFormSubmit = useCallback(() => {
    onClose();

    void handleEasebuzzPayment();
  }, [handleEasebuzzPayment, onClose]);

  // ---------------------------------------------------------
  // UI
  // ---------------------------------------------------------

  return (
    <>
      <Dialog
        open={visible}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 7 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700 }}
              >
                Recharge Your Wallet
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Current Wallet Amt: ₹{balance.toFixed(2)}
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              size="small"
              aria-label="close wallet dialog"
            >
              <IconX stroke={1.5} size="18px" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack
            spacing={2.5}
            component="form"
            onSubmit={(event) => {
              event.preventDefault();
              void handleFormSubmit();
            }}
          >
            {/* Amount Section */}

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderColor: "primary.light",
                bgcolor: "primary.50",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Enter amount in multiples of 100
              </Typography>

              <TextField
                label="Amount"
                type="number"
                value={amount}
                onChange={(event) =>
                  setAmount(
                    Number(event.target.value || 0)
                  )
                }
                inputProps={{
                  min: 250,
                  max: 50000,
                }}
                helperText="Min value: ₹250 & Max Value: ₹50,000"
                fullWidth
              />

              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                sx={{ mt: 2 }}
              >
                {predefinedAmounts.map(
                  (presetAmount) => (
                    <Chip
                      key={presetAmount}
                      label={`₹ ${presetAmount}`}
                      variant={
                        amount === presetAmount
                          ? "filled"
                          : "outlined"
                      }
                      color="primary"
                      onClick={() =>
                        setAmount(presetAmount)
                      }
                      clickable
                    />
                  )
                )}
              </Stack>
            </Paper>

            {/* Payment Summary */}

            <Paper
              variant="outlined"
              sx={{ p: 2 }}
            >
              <Stack spacing={1.25}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Recharge Amount
                  </Typography>

                  <Typography variant="body2">
                    ₹ {amount}
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Coupon Discount
                  </Typography>

                  <Typography variant="body2">
                    ₹ {discount}
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Payment Gateway Charges (0%)
                  </Typography>

                  <Typography variant="body2">
                    ₹ {gatewayCharge}
                  </Typography>
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700 }}
                  >
                    Payable Amount
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700 }}
                  >
                    ₹ {payableAmount}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            {/* Payment Button */}

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pt: 0.5,
              }}
            >
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loader}
                sx={{
                  minWidth: 220,
                  borderRadius: 2,
                }}
              >
                {loader
                  ? "Processing..."
                  : "Continue to Payment"}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}

      <Dialog
        open={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          onClose();
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Payment Successful
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2">
            Your payment was successful and the amount
            will be added to your wallet shortly.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RechargeWalletEzzBuzz;